import { Button, DatePicker, message, Select, Space, Table, Input, Checkbox } from "antd";
import dayjs from "dayjs";
import { useEffect, useState, useMemo } from "react";
import { serverFetch } from "../../server";
import { fmtDate, strDate2Server } from "../../extensions/convert";
import { renderCheckbox } from "../../extensions/helper";
import { useSelector } from "react-redux";
import { progKeysStatusRender } from "../../extensions/enums";
import LicenseOrderKey from "./license-order-key";
import "./license-keys.css";

const vidPerOptions = [
  { value: 0, label: "Все" },
  { value: 1, label: "Период активации" },
  { value: 2, label: "Период окончания" },
];

const LicenseKeys = () => {
  const [filter, setFilter] = useState({
    dateBeg: dayjs(),
    dateEnd: dayjs(),
    vidPer: 0,
    searchText: "",
    allCompanies: false,
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const user = useSelector((s) => s.user);
  
  // Загружаем сохраненные настройки сортировки из localStorage
  const loadSortSettings = () => {
    try {
      const saved = localStorage.getItem('licenseKeys_sort');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          sortField: parsed.sortField || null,
          sortOrder: parsed.sortOrder || null,
        };
      }
    } catch (e) {
      console.error('Error loading sort settings from localStorage:', e);
    }
    return { sortField: null, sortOrder: null };
  };

  const [sortSettings, setSortSettings] = useState(loadSortSettings);

  // Сохраняем настройки сортировки в localStorage
  const saveSortSettings = (field, order) => {
    try {
      const toSave = { sortField: field, sortOrder: order };
      localStorage.setItem('licenseKeys_sort', JSON.stringify(toSave));
      setSortSettings(toSave);
    } catch (e) {
      console.error('Error saving sort settings to localStorage:', e);
    }
  };

  const getData = () => {
    setLoading(true);
    
    let url = `license/key`;
    let hasParams = false;
    
    // Добавляем параметры периода только если выбран конкретный вид периода
    if (filter.vidPer > 0) {
      url += `?vidPer=${filter.vidPer}&dateBeg=${strDate2Server(filter.dateBeg)}&dateEnd=${strDate2Server(filter.dateEnd)}`;
      hasParams = true;
    }
    
    // Добавляем companyId для админов если чекбокс "По всем компаниям" не отмечен
    if (user?.admin && !filter.allCompanies && user?.companyId) {
      const separator = hasParams ? '&' : '?';
      url += `${separator}companyId=${user.companyId}`;
      hasParams = true;
    }
    
    // Добавляем поисковый текст если указан
    if (filter.searchText.trim()) {
      const separator = hasParams ? '&' : '?';
      url += `${separator}searchText=${encodeURIComponent(filter.searchText.trim())}`;
    }
    
    serverFetch(url)
      .then((data) => {
        // console.log(data)
        setLoading(false);
        setItems(data.map(i => ({...i, keyId: `${i.companyId || ''}/${i.accId || ''}/${i.id || ''}`})));
      })
      .catch((e) => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(getData, []);

  // Функции сортировки для разных типов данных
  const stringSorter = (a, b, dataIndex) => {
    const aVal = a[dataIndex] || '';
    const bVal = b[dataIndex] || '';
    return aVal.localeCompare(bVal, 'ru', { numeric: true, sensitivity: 'base' });
  };

  const dateSorter = (a, b, dataIndex) => {
    const aVal = a[dataIndex] ? new Date(a[dataIndex]).getTime() : 0;
    const bVal = b[dataIndex] ? new Date(b[dataIndex]).getTime() : 0;
    return aVal - bVal;
  };

  const numberSorter = (a, b, dataIndex) => {
    const aVal = a[dataIndex] ?? 0;
    const bVal = b[dataIndex] ?? 0;
    return aVal - bVal;
  };

  const booleanSorter = (a, b, dataIndex) => {
    const aVal = a[dataIndex] ? 1 : 0;
    const bVal = b[dataIndex] ? 1 : 0;
    return aVal - bVal;
  };

  const columns = useMemo(() => {
    const baseColumns = [
      { 
        dataIndex: "companyId", 
        title: "#", 
        width: 120, 
        render: (text, record) => `${record.companyId || ''}/${record.accId || ''}/${record.id || ''}`,
        sorter: (a, b) => {
          const aVal = `${a.companyId || ''}/${a.accId || ''}/${a.id || ''}`;
          const bVal = `${b.companyId || ''}/${b.accId || ''}/${b.id || ''}`;
          return aVal.localeCompare(bVal, 'ru', { numeric: true });
        },
        sortOrder: sortSettings.sortField === 'companyId' ? sortSettings.sortOrder : null,
      },
      ...(user?.admin ? [{ 
        dataIndex: "companyName", 
        title: "Компания", 
        width: 200,
        sorter: (a, b) => stringSorter(a, b, 'companyName'),
        sortOrder: sortSettings.sortField === 'companyName' ? sortSettings.sortOrder : null,
      }] : []),
      { 
        dataIndex: "docDate", 
        title: "Дата", 
        render: fmtDate,
        sorter: (a, b) => dateSorter(a, b, 'docDate'),
        sortOrder: sortSettings.sortField === 'docDate' ? sortSettings.sortOrder : null,
      },
      { 
        dataIndex: "progName", 
        title: "Программа",
        sorter: (a, b) => stringSorter(a, b, 'progName'),
        sortOrder: sortSettings.sortField === 'progName' ? sortSettings.sortOrder : null,
      },
      { 
        dataIndex: "licName", 
        title: "Лицензия",
        sorter: (a, b) => stringSorter(a, b, 'licName'),
        sortOrder: sortSettings.sortField === 'licName' ? sortSettings.sortOrder : null,
      },
      { 
        dataIndex: "status", 
        title: "Статус", 
        render: progKeysStatusRender,
        sorter: (a, b) => numberSorter(a, b, 'status'),
        sortOrder: sortSettings.sortField === 'status' ? sortSettings.sortOrder : null,
      },
      { 
        dataIndex: "startDate", 
        title: "Актив.", 
        render: fmtDate,
        sorter: (a, b) => dateSorter(a, b, 'startDate'),
        sortOrder: sortSettings.sortField === 'startDate' ? sortSettings.sortOrder : null,
      },
      { 
        dataIndex: "finDate", 
        title: "Истек.", 
        render: fmtDate,
        sorter: (a, b) => dateSorter(a, b, 'finDate'),
        sortOrder: sortSettings.sortField === 'finDate' ? sortSettings.sortOrder : null,
      },
      { 
        dataIndex: "comment", 
        title: "Комментарий",
        sorter: (a, b) => stringSorter(a, b, 'comment'),
        sortOrder: sortSettings.sortField === 'comment' ? sortSettings.sortOrder : null,
      },
      { 
        dataIndex: "workGuid", 
        title: "Рабочая станция",
        sorter: (a, b) => stringSorter(a, b, 'workGuid'),
        sortOrder: sortSettings.sortField === 'workGuid' ? sortSettings.sortOrder : null,
      },
      { 
        dataIndex: "frozen", 
        title: "Заморожен",
        width: 100,
        minWidth: 100,
        render: renderCheckbox,
        sorter: (a, b) => booleanSorter(a, b, 'frozen'),
        sortOrder: sortSettings.sortField === 'frozen' ? sortSettings.sortOrder : null,
      },
      { 
        dataIndex: "sent", 
        title: "Отправлен",
        width: 100,
        minWidth: 100,
        render: renderCheckbox,
        sorter: (a, b) => booleanSorter(a, b, 'sent'),
        sortOrder: sortSettings.sortField === 'sent' ? sortSettings.sortOrder : null,
      },
    ];
    return baseColumns;
  }, [sortSettings, user?.admin]);

  const onFilterChange = (value) => setFilter({ ...filter, ...value });

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      getData();
    }
  };

  const onRowClick = (record) => {
    setSelectedKey({
      companyId: record.companyId,
      accId: record.accId,
      keyId: record.id,
    });
  };

  const handleKeyModalClose = refresh => {
    setSelectedKey(null);
    refresh && getData();
  };

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100vh', width: '100%', overflow: 'hidden' }}>
      <Space style={{ flexShrink: 0, marginBottom: 16 }}>
        <Select
          options={vidPerOptions}
          value={filter.vidPer}
          onChange={(e) => onFilterChange({ vidPer: e })}
          style={{ width: 200 }}
        />
        {filter.vidPer > 0 && (
          <>
            <div>Период с</div>
            <DatePicker
              format="DD.MM.YYYY"
              value={filter.dateBeg}
              onChange={(e) => onFilterChange({ dateBeg: e })}
              allowClear={false}
            />
            <div>по</div>
            <DatePicker
              format="DD.MM.YYYY"
              value={filter.dateEnd}
              onChange={(e) => onFilterChange({ dateEnd: e })}
              allowClear={false}
            />
          </>
        )}
        <Input
          placeholder="Поиск по компании, ИНН, телефону, email, ключу, GUID, пользователю"
          value={filter.searchText}
          onChange={(e) => setFilter({ ...filter, searchText: e.target.value })}
          onKeyPress={handleKeyPress}
          style={{ width: 400 }}
        />
        {user?.admin && (
          <Checkbox
            checked={filter.allCompanies}
            onChange={(e) => setFilter({ ...filter, allCompanies: e.target.checked })}
          >
            По всем компаниям
          </Checkbox>
        )}
        <Button type="primary" onClick={getData} loading={loading}>
          Обновить
        </Button>
      </Space>
      <div style={{ flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'hidden', width: '100%' }}>
        <Table
          className="license-keys-table"
          columns={columns}
          dataSource={items}
          loading={loading}
          rowKey="keyId"
          pagination={false}
          size="small"
          sortDirections={['ascend', 'descend', 'ascend']}
          scroll={{ x: 'max-content', y: 'calc(100vh - 240px)' }}
          onChange={(pagination, filters, sorter) => {
            if (sorter.field) {
              saveSortSettings(sorter.field, sorter.order);
            } else {
              saveSortSettings(null, null);
            }
          }}
          onRow={(record) => ({
            onClick: () => onRowClick(record),
            style: { cursor: 'pointer' }
          })}
        />
      </div>

      {selectedKey && (
        <LicenseOrderKey
          companyId={selectedKey.companyId}
          accId={selectedKey.accId}
          keyId={selectedKey.keyId}
          onClose={handleKeyModalClose}
        />
      )}
    </div>
  );
};

export default LicenseKeys;
